const WOMAN_SEAT_CATEGORIES = ['woman_seat', 'woman_seat_tv'];
const GENERAL_SEAT_CATEGORIES = ['seat', 'seat_tv', 'seat_handicap', 'hallway_seat'];
const PET_FRIENDLY_SEAT_CATEGORIES = ['pet_seat', 'pet_seat_tv', 'pet_friendly'];
const SELECTABLE_CATEGORIES = [
  ...WOMAN_SEAT_CATEGORIES,
  ...GENERAL_SEAT_CATEGORIES,
  ...PET_FRIENDLY_SEAT_CATEGORIES,
];

/**
 * Checks if a category represents a selectable seat.
 * @param {string} category - The seat category to check.
 * @returns {boolean} True if the category is a selectable seat.
 */
export const isSeat = (category) => SELECTABLE_CATEGORIES.includes(category);

/**
 * Checks if a category represents a woman-only seat.
 * @param {string} category - The seat category to check.
 * @returns {boolean} True if the category is a woman seat.
 */
export const isWomanSeat = (category) => WOMAN_SEAT_CATEGORIES.includes(category);

/**
 * Checks if a category represents a pet-friendly seat.
 * @param {string} category - The seat category to check.
 * @returns {boolean} True if the category is pet-friendly.
 */
export const isPetFriendlySeat = (category) => PET_FRIENDLY_SEAT_CATEGORIES.includes(category);

/**
 * Normalizes seat category coming from either category or type fields.
 * @param {Object} space - The space object containing category or type.
 * @returns {string} The normalized seat category.
 */
export const getSeatCategory = (space) => (space && (space.category || space.type)) || '';

/**
 * Checks whether a seat is blocked by a selected handicap seat's ADA group.
 * Looks for any selected seat_handicap whose adaSeatGroup.seats includes this seat's number
 * (on the same seatLevel), excluding the handicap seat itself.
 * @param {Array} selectedSeats - Currently selected seats.
 * @param {string|number} number - Seat number to check.
 * @param {string|number} seatLevel - Seat level/deck.
 * @returns {boolean}
 */
export const isBlockedByAdaGroup = (selectedSeats, number, seatLevel) => {
  if (!selectedSeats) return false;
  const num = String(number);
  const level = String(seatLevel);

  return selectedSeats.some((seat) => {
    if (seat.category !== 'seat_handicap') return false;
    const adaSeats = seat.adaSeatGroup && seat.adaSeatGroup.seats;
    if (!Array.isArray(adaSeats)) return false;
    if (String(seat.seatLevel) !== level) return false;
    return adaSeats.some((n) => String(n) === num && num !== String(seat.number));
  });
};

/**
 * Removes any selected seats that belong to the given seat's ADA companion group on
 * the same seatLevel. The seat itself is preserved. Callers should only invoke this
 * for `seat_handicap` seats; a missing/invalid `adaSeatGroup.seats` is a no-op.
 * @param {Array} selected - Current selected seats list.
 * @param {Object} seat - The handicap seat being added/toggled.
 * @returns {Array} New list with companions filtered out.
 */
export const evictAdaCompanions = (selected, seat) => {
  if (!Array.isArray(seat.adaSeatGroup?.seats)) return selected;
  const adaNumbers = seat.adaSeatGroup.seats.map(String);
  return selected.filter(
    (s) =>
      String(s.number) === String(seat.number) ||
      String(s.seatLevel) !== String(seat.seatLevel) ||
      !adaNumbers.includes(String(s.number)),
  );
};

/**
 * Checks if a seat is unavailable, either because it's already sold/reserved
 * or because it's blocked for an intermediate (connecting) route segment.
 * `intermediateSeat` is a string flag ("1"/"0"), not a boolean.
 * @param {Object} seat - The seat object.
 * @returns {boolean} True if the seat should render and behave as occupied.
 */
export const isSeatOccupied = (seat) => seat.occupied === true || seat.intermediateSeat === '1';
